import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Button,
  Chip,
  Divider,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  InputBase
} from '@mui/material';
import { 
  Search as SearchIcon,
  FilterList as FilterIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

function QuoteHistory() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Only fetch quotes if user is logged in
        if (currentUser && currentUser.uid) {
          // Import the API service
          const api = (await import('../services/api')).default;
          
          // Fetch quotes from the API
          const response = await api.getQuotes(currentUser.uid);
          console.log('Quotes response:', response.data);
          
          if (response.data && Array.isArray(response.data)) {
            // Format quotes for display
            const formattedQuotes = response.data.map(quote => ({
              id: quote.id,
              serviceType: quote.job_type || 'Unknown Service',
              date: new Date(quote.created_at || Date.now()).toLocaleDateString(),
              amount: quote.amount || 0,
              status: quote.status || 'pending'
            }));
            
            setQuotes(formattedQuotes);
            setFilteredQuotes(formattedQuotes);
          } else {
            // Empty array if no quotes or invalid response
            setQuotes([]);
            setFilteredQuotes([]);
          }
        } else {
          // User not logged in
          setQuotes([]);
          setFilteredQuotes([]);
        }
      } catch (err) {
        console.error('Error fetching quotes:', err);
        setError('Failed to load quotes. Please try again later.');
        setQuotes([]);
        setFilteredQuotes([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchQuotes();
  }, [currentUser]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = quotes.filter(quote => 
        quote.serviceType.toLowerCase().includes(searchTerm.toLowerCase()) ||
        quote.status.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredQuotes(filtered);
    } else {
      setFilteredQuotes(quotes);
    }
    setPage(0);
  }, [searchTerm, quotes]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending':
        return 'warning';
      case 'Accepted':
        return 'success';
      case 'Completed':
        return 'info';
      case 'Rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Quote History
      </Typography>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <InputBase
                placeholder="Search quotes..."
                value={searchTerm}
                onChange={handleSearch}
                sx={{ flex: 1 }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={8} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            <Button 
              component={RouterLink} 
              to="/request-quote" 
              variant="contained"
              startIcon={<FilterIcon />}
              sx={{ ml: 1 }}
            >
              Request New Quote
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="quotes table">
          <TableHead>
            <TableRow>
              <TableCell>Service Type</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredQuotes
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((quote) => (
                <TableRow
                  key={quote.id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {quote.serviceType}
                  </TableCell>
                  <TableCell>{quote.date}</TableCell>
                  <TableCell>${quote.amount.toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip 
                      label={quote.status} 
                      color={getStatusColor(quote.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      component={RouterLink}
                      to={`/quotes/${quote.id}`}
                      color="primary"
                      size="small"
                    >
                      <ArrowForwardIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            {filteredQuotes.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body1" sx={{ py: 2 }}>
                    No quotes found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredQuotes.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
}

export default QuoteHistory;
